import React, { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import swal from 'sweetalert';
import { useHistory } from 'react-router-dom';
import Sidebar from './Sidebar';
import MetaTag from '../layout/MetaTag';
import Loader from '../layout/loader';
import { getCategoryIcon } from '../components/marketplace/CategoryIcons';
import {
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  MenuItem,
  Chip,
  IconButton
} from '@material-ui/core';

import AddCircleOutlineIcon from '@material-ui/icons/AddCircleOutline';
import EditIcon from '@material-ui/icons/Edit';
import DeleteOutlineIcon from '@material-ui/icons/DeleteOutline';
import CallMergeIcon from '@material-ui/icons/CallMerge';
import SearchIcon from '@material-ui/icons/Search';
import RefreshIcon from '@material-ui/icons/Refresh';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import ChevronRightIcon from '@material-ui/icons/ChevronRight';
import VisibilityOutlinedIcon from '@material-ui/icons/VisibilityOutlined';

import './adminRoutes.css';

function CategoryManager() {
  const history = useHistory();

  const [tree, setTree] = useState([]);
  const [flatCategories, setFlatCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('all'); // 'all' | 'active' | 'inactive'
  const [expandedNodes, setExpandedNodes] = useState({});

  // Category Create / Edit Modal State
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingCategory, setEditingCategory] = useState(null);
  const [formName, setFormName] = useState('');
  const [formSlug, setFormSlug] = useState('');
  const [formParentId, setFormParentId] = useState('');
  const [formLevel, setFormLevel] = useState(1);
  const [formType, setFormType] = useState('CATEGORY');
  const [formDescription, setFormDescription] = useState('');
  const [formIcon, setFormIcon] = useState('PackageIcon');
  const [formStatus, setFormStatus] = useState('active');
  const [formIsFeatured, setFormIsFeatured] = useState(false);
  const [formDisplayOrder, setFormDisplayOrder] = useState(0);
  const [formAttributes, setFormAttributes] = useState([]);
  const [formTags, setFormTags] = useState([]);
  const [tagInput, setTagInput] = useState('');

  // Attribute item editor state
  const [newAttrName, setNewAttrName] = useState('');
  const [newAttrType, setNewAttrType] = useState('text');
  const [newAttrOptions, setNewAttrOptions] = useState('');
  const [newAttrRequired, setNewAttrRequired] = useState(false);

  // Merge Modal State
  const [isMergeModalOpen, setIsMergeModalOpen] = useState(false);
  const [mergeSourceId, setMergeSourceId] = useState('');
  const [mergeTargetId, setMergeTargetId] = useState('');

  const fetchAllData = async () => {
    try {
      setLoading(true);
      const [treeRes, flatRes] = await Promise.all([
        axios.get('/api/admin/taxonomy/tree?status=all'),
        axios.get('/api/categories?status=all')
      ]);

      const t = treeRes.data.tree || [];
      setTree(t);
      setFlatCategories(flatRes.data.categories || []);

      // Auto expand first 5 categories
      const initialExp = {};
      t.slice(0, 5).forEach(c => { initialExp[c._id] = true; });
      setExpandedNodes(prev => ({ ...initialExp, ...prev }));
    } catch (err) {
      console.error("Failed to load category management data:", err);
      swal("Error", "Could not load categories.", "error");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAllData();
  }, []);

  const toggleNode = (id) => {
    setExpandedNodes(prev => ({ ...prev, [id]: !prev[id] }));
  };

  const handleOpenCreateModal = (parent = null) => {
    setEditingCategory(null);
    setFormName('');
    setFormSlug('');
    setFormDescription('');
    setFormIcon('PackageIcon');
    setFormStatus('active');
    setFormIsFeatured(false);
    setFormDisplayOrder(flatCategories.length + 1);
    setFormAttributes([]);
    setFormTags([]);
    setTagInput('');

    if (parent) {
      setFormParentId(parent._id);
      setFormLevel(parent.level + 1);
      setFormType(parent.level === 1 ? 'SUBCATEGORY' : 'PRODUCT_TYPE');
    } else {
      setFormParentId('');
      setFormLevel(1);
      setFormType('CATEGORY');
    }
    setIsModalOpen(true);
  };

  const handleOpenEditModal = (category) => {
    setEditingCategory(category);
    setFormName(category.name || '');
    setFormSlug(category.slug || '');
    setFormParentId(category.parentId?._id || category.parentId || '');
    setFormLevel(category.level || 1);
    setFormType(category.type || 'CATEGORY');
    setFormDescription(category.description || category.desc || '');
    setFormIcon(category.icon || 'PackageIcon');
    setFormStatus(category.status || 'active');
    setFormIsFeatured(!!category.isFeatured);
    setFormDisplayOrder(category.displayOrder || 0);
    setFormAttributes(category.attributes || []);
    setFormTags(category.tags || []);
    setTagInput('');
    setIsModalOpen(true);
  };

  const handleAddAttribute = () => {
    if (!newAttrName.trim()) return;
    const opts = newAttrOptions ? newAttrOptions.split(',').map(o => o.trim()).filter(Boolean) : [];
    setFormAttributes([
      ...formAttributes,
      {
        name: newAttrName.trim(),
        type: newAttrType,
        options: opts,
        isRequired: newAttrRequired
      }
    ]);
    setNewAttrName('');
    setNewAttrType('text');
    setNewAttrOptions('');
    setNewAttrRequired(false);
  };

  const handleRemoveAttribute = (idx) => {
    setFormAttributes(formAttributes.filter((_, i) => i !== idx));
  };

  const handleAddTag = (e) => {
    if (e.key === 'Enter' || e.type === 'click') {
      e.preventDefault();
      const val = tagInput.trim();
      if (val && !formTags.includes(val)) {
        setFormTags([...formTags, val]);
        setTagInput('');
      }
    }
  };

  const handleRemoveTag = (t) => {
    setFormTags(formTags.filter(item => item !== t));
  };

  const handleSaveCategory = async (e) => {
    e.preventDefault();
    if (!formName.trim()) {
      return swal("Validation", "Category name is required.", "warning");
    }

    try {
      const payload = {
        name: formName.trim(),
        slug: formSlug ? formSlug.trim() : undefined,
        parentId: formParentId || null,
        level: formLevel,
        type: formType,
        description: formDescription.trim(),
        icon: formIcon,
        status: formStatus,
        isFeatured: formIsFeatured,
        displayOrder: Number(formDisplayOrder) || 0,
        attributes: formAttributes,
        tags: formTags
      };

      if (editingCategory) {
        await axios.put(`/api/admin/categories/${editingCategory._id}`, payload);
        swal("Updated", "Category updated successfully.", "success");
      } else {
        await axios.post('/api/admin/categories', payload);
        swal("Created", "Category created successfully.", "success");
      }

      setIsModalOpen(false);
      fetchAllData();
    } catch (err) {
      swal("Error", err.response?.data?.msg || "Failed to save category.", "error");
    }
  };

  const handleArchiveCategory = (cat) => {
    swal({
      title: `Delete / Archive "${cat.name}"?`,
      text: `If products belong to this category, it will be safely archived without data loss. If 0 products exist, it will be permanently deleted.`,
      icon: "warning",
      buttons: ["Cancel", "Confirm Delete"],
      dangerMode: true,
    }).then(async (willArchive) => {
      if (willArchive) {
        try {
          const res = await axios.delete(`/api/admin/categories/${cat._id}`);
          swal("Archived", res.data.message || "Category archived successfully.", "success");
          fetchAllData();
        } catch (err) {
          swal("Error", err.response?.data?.msg || "Failed to archive category.", "error");
        }
      }
    });
  };

  const handleMergeSubmit = async (e) => {
    e.preventDefault();
    if (!mergeSourceId || !mergeTargetId) {
      return swal("Validation", "Please select both source and target categories.", "warning");
    }

    try {
      const res = await axios.post('/api/admin/categories/merge', {
        sourceCategoryId: mergeSourceId,
        targetCategoryId: mergeTargetId
      });
      swal("Merged", res.data.message || "Categories merged successfully.", "success");
      setIsMergeModalOpen(false);
      fetchAllData();
    } catch (err) {
      swal("Error", err.response?.data?.msg || "Failed to merge categories.", "error");
    }
  };

  const filteredTree = useMemo(() => {
    let list = [...tree];
    const q = searchQuery.toLowerCase().trim();

    if (q) {
      list = list.filter(l1 => {
        const l1Match = l1.name.toLowerCase().includes(q) || l1.slug?.toLowerCase().includes(q);
        const l2Match = (l1.children || []).some(l2 =>
          l2.name.toLowerCase().includes(q) ||
          l2.slug?.toLowerCase().includes(q) ||
          (l2.children || []).some(l3 => l3.name.toLowerCase().includes(q))
        );
        return l1Match || l2Match;
      });
    }

    if (statusFilter === 'active') {
      list = list.filter(c => c.status === 'active');
    } else if (statusFilter === 'inactive') {
      list = list.filter(c => c.status === 'inactive' || c.status === 'archived');
    }

    return list;
  }, [tree, searchQuery, statusFilter]);

  return (
    <div className="dashboard-container">
      <MetaTag
        title="Categories | IMSHOP Admin"
        description="Marketplace categories management"
      />
      <Sidebar />

      <main className="main-container admin-dashboard-main">
        {/* ═══════════════════════ 1. PAGE HEADER ═══════════════════════ */}
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", flexWrap: "wrap", gap: "16px", marginBottom: "20px" }}>
          <div>
            <span style={{ fontSize: "11px", fontWeight: "800", color: "#8b2626", letterSpacing: "1.2px", textTransform: "uppercase", display: "inline-block", marginBottom: "4px" }}>
              CATEGORY MANAGEMENT
            </span>
            <h1 style={{ fontSize: "24px", fontWeight: "900", color: "#111315", margin: "0 0 4px", letterSpacing: "-0.5px" }}>
              Categories
            </h1>
            <p style={{ fontSize: "13.5px", color: "#6b7280", margin: 0 }}>
              Manage the marketplace category structure used by sellers, products, search and customer discovery.
            </p>
          </div>

          <div style={{ display: "flex", gap: "10px", alignItems: "center", flexWrap: "wrap" }}>
            <Button
              variant="outlined"
              onClick={() => setIsMergeModalOpen(true)}
              style={{ fontSize: "12.5px", fontWeight: "700", textTransform: "none", borderColor: "#d1d5db", background: "#ffffff", color: "#111315", padding: "7.5px 14px", borderRadius: "8px" }}
              startIcon={<CallMergeIcon />}
            >
              Merge Duplicate Categories
            </Button>
            <Button
              variant="contained"
              onClick={() => handleOpenCreateModal(null)}
              style={{ background: "#8b2626", color: "#ffffff", fontSize: "12.5px", fontWeight: "800", textTransform: "none", padding: "8px 16px", borderRadius: "8px" }}
              startIcon={<AddCircleOutlineIcon />}
            >
              + Create Category
            </Button>
          </div>
        </div>

        {/* ═══════════════════════ 2. SEARCH / TOOLBAR ═══════════════════════ */}
        <div style={{ background: "#ffffff", border: "1px solid #edf0f4", borderRadius: "10px", padding: "12px 16px", display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: "12px", marginBottom: "20px" }}>
          <div style={{ position: "relative", flex: 1, minWidth: "260px" }}>
            <SearchIcon style={{ position: "absolute", left: "10px", top: "9px", color: "#9ca3af", fontSize: "19px" }} />
            <input
              type="text"
              style={{ width: "100%", padding: "8px 12px 8px 36px", border: "1.5px solid #d1d5db", borderRadius: "8px", fontSize: "13px", background: "#f9fafb", outline: "none", boxSizing: "border-box" }}
              placeholder="Search categories, subcategories..."
              value={searchQuery}
              onChange={e => setSearchQuery(e.target.value)}
            />
          </div>

          <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
            {['all', 'active', 'inactive'].map(s => (
              <button
                key={s}
                type="button"
                onClick={() => setStatusFilter(s)}
                style={{
                  background: statusFilter === s ? "#111315" : "#ffffff",
                  color: statusFilter === s ? "#ffffff" : "#4b5563",
                  border: "1px solid",
                  borderColor: statusFilter === s ? "#111315" : "#d1d5db",
                  borderRadius: "99px",
                  padding: "5px 12px",
                  fontSize: "12px",
                  fontWeight: "700",
                  cursor: "pointer"
                }}
              >
                {s === 'all' ? 'All' : s === 'active' ? 'Active' : 'Inactive'}
              </button>
            ))}

            <Button
              variant="outlined"
              onClick={fetchAllData}
              startIcon={<RefreshIcon />}
              style={{ fontSize: "12px", textTransform: "none", borderColor: "#d1d5db", background: "#ffffff", padding: "5px 12px", borderRadius: "8px" }}
            >
              Refresh
            </Button>
          </div>
        </div>

        {/* ═══════════════════════ 3. CATEGORY CONTENT ═══════════════════════ */}
        {loading ? (
          <Loader />
        ) : filteredTree.length > 0 ? (
          <div style={{ display: "flex", flexDirection: "column", gap: "12px" }}>
            {filteredTree.map(cat => {
              const isExpanded = !!expandedNodes[cat._id];
              const subCount = cat.children ? cat.children.length : 0;
              const prodCount = cat.productCount || 0;

              return (
                <div
                  key={cat._id}
                  style={{
                    background: "#ffffff",
                    border: "1px solid #edf0f4",
                    borderRadius: "12px",
                    overflow: "hidden",
                    boxShadow: "0 1px 3px rgba(0,0,0,0.02)"
                  }}
                >
                  {/* Category Primary Card Header */}
                  <div style={{ padding: "16px 20px", display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: "12px" }}>
                    <div style={{ display: "flex", alignItems: "center", gap: "12px", flex: 1, minWidth: "260px" }}>
                      <button
                        type="button"
                        onClick={() => toggleNode(cat._id)}
                        style={{ background: "transparent", border: "none", cursor: "pointer", padding: "4px", color: "#6b7280", display: "flex", alignItems: "center" }}
                        title={isExpanded ? "Collapse subcategories" : "Expand subcategories"}
                      >
                        {subCount > 0 ? (
                          isExpanded ? <ExpandMoreIcon fontSize="small" /> : <ChevronRightIcon fontSize="small" />
                        ) : (
                          <span style={{ width: "20px" }} />
                        )}
                      </button>

                      <div style={{ width: "36px", height: "36px", borderRadius: "8px", background: "#fdf6f5", color: "#8b2626", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                        {getCategoryIcon(cat, 20)}
                      </div>

                      <div>
                        <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                          <strong style={{ fontSize: "15px", color: "#111315" }}>{cat.name}</strong>
                          <span style={{ fontSize: "12px", color: "#9ca3af", fontFamily: "monospace" }}>/{cat.slug}</span>
                          {cat.isFeatured && (
                            <span style={{ background: "#faedea", color: "#8b2626", fontSize: "10px", fontWeight: "800", padding: "1px 6px", borderRadius: "4px" }}>
                              Featured
                            </span>
                          )}
                        </div>
                        <p style={{ fontSize: "12.5px", color: "#6b7280", margin: "2px 0 0" }}>
                          {cat.description || cat.desc || "Standard marketplace department"}
                        </p>
                      </div>
                    </div>

                    <div style={{ display: "flex", alignItems: "center", gap: "14px", flexWrap: "wrap" }}>
                      <div style={{ display: "flex", alignItems: "center", gap: "6px" }}>
                        <span style={{ background: "#f3f4f6", color: "#374151", fontSize: "12px", fontWeight: "700", padding: "4px 9px", borderRadius: "6px" }}>
                          {prodCount} Products
                        </span>
                        <span style={{ background: "#eff6ff", color: "#1e40af", fontSize: "12px", fontWeight: "700", padding: "4px 9px", borderRadius: "6px" }}>
                          {subCount} Subcategories
                        </span>
                        <span style={{
                          background: cat.status === 'active' ? "#dcfce7" : "#fee2e2",
                          color: cat.status === 'active' ? "#166534" : "#991b1b",
                          fontSize: "11px",
                          fontWeight: "800",
                          textTransform: "uppercase",
                          padding: "3px 8px",
                          borderRadius: "4px"
                        }}>
                          {cat.status || 'active'}
                        </span>
                      </div>

                      <div style={{ display: "inline-flex", alignItems: "center", gap: "4px" }}>
                        <button
                          type="button"
                          onClick={() => history.push(`/Admin/dashboard/Products?category=${encodeURIComponent(cat.name)}`)}
                          style={{ background: "#f9fafb", border: "1px solid #d1d5db", borderRadius: "6px", padding: "4px 8px", fontSize: "11.5px", fontWeight: "700", color: "#374151", cursor: "pointer", display: "inline-flex", alignItems: "center", gap: "4px" }}
                        >
                          <VisibilityOutlinedIcon style={{ fontSize: "14px" }} />
                          <span>View</span>
                        </button>
                        <button
                          type="button"
                          onClick={() => handleOpenCreateModal(cat)}
                          style={{ background: "#ffffff", border: "1px solid #d1d5db", borderRadius: "6px", padding: "4px 8px", fontSize: "11.5px", fontWeight: "700", color: "#8b2626", cursor: "pointer" }}
                        >
                          + Subcategory
                        </button>
                        <IconButton size="small" onClick={() => handleOpenEditModal(cat)} title="Edit Category">
                          <EditIcon fontSize="small" />
                        </IconButton>
                        <IconButton size="small" onClick={() => handleArchiveCategory(cat)} title="Delete / Archive Category">
                          <DeleteOutlineIcon fontSize="small" style={{ color: "#dc2626" }} />
                        </IconButton>
                      </div>
                    </div>
                  </div>

                  {/* Subcategories (Indented Rows) */}
                  {isExpanded && subCount > 0 && (
                    <div style={{ background: "#fafaf9", borderTop: "1px solid #edf0f4", padding: "4px 0" }}>
                      {cat.children.map(sub => (
                        <div
                          key={sub._id}
                          style={{
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "space-between",
                            padding: "8px 20px 8px 56px",
                            borderBottom: "1px solid #f0f0ee",
                            fontSize: "13px"
                          }}
                        >
                          <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                            <span style={{ color: "#9ca3af" }}>├─</span>
                            <strong style={{ color: "#374151" }}>{sub.name}</strong>
                            <span style={{ fontSize: "11.5px", color: "#9ca3af", fontFamily: "monospace" }}>/{sub.slug}</span>
                          </div>

                          <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
                            <span style={{ fontSize: "11.5px", color: "#6b7280", background: "#ffffff", border: "1px solid #e5e7eb", padding: "2px 6px", borderRadius: "4px" }}>
                              {sub.productCount || 0} prods
                            </span>
                            <IconButton size="small" onClick={() => handleOpenEditModal(sub)} title="Edit Subcategory">
                              <EditIcon fontSize="small" />
                            </IconButton>
                            <IconButton size="small" onClick={() => handleArchiveCategory(sub)} title="Delete Subcategory">
                              <DeleteOutlineIcon fontSize="small" style={{ color: "#dc2626" }} />
                            </IconButton>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        ) : (
          <div style={{ background: "#ffffff", border: "1px solid #edf0f4", borderRadius: "12px", padding: "48px", textAlign: "center" }}>
            <h3 style={{ fontSize: "16px", fontWeight: "800", color: "#111315", margin: 0 }}>
              No categories found
            </h3>
            <p style={{ fontSize: "13px", color: "#6b7280", marginTop: "4px" }}>
              Try searching with another keyword or create a new category above.
            </p>
          </div>
        )}

        {/* ═══════════════════════ MODALS ═══════════════════════ */}
        {/* MODAL 1: CREATE / EDIT CATEGORY */}
        <Dialog open={isModalOpen} onClose={() => setIsModalOpen(false)} maxWidth="md" fullWidth>
          <DialogTitle style={{ borderBottom: "1px solid #edf0f4" }}>
            <strong>{editingCategory ? `Edit Category: ${editingCategory.name}` : `Create New Taxonomy Category (Level ${formLevel})`}</strong>
          </DialogTitle>
          <form onSubmit={handleSaveCategory}>
            <DialogContent style={{ padding: "20px" }}>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "14px", marginBottom: "14px" }}>
                <TextField
                  label="Category Name *"
                  fullWidth
                  variant="outlined"
                  size="small"
                  value={formName}
                  onChange={e => setFormName(e.target.value)}
                  placeholder="e.g. Footwear, Electronics, Men's Fashion"
                />

                <TextField
                  label="Slug (Optional)"
                  fullWidth
                  variant="outlined"
                  size="small"
                  value={formSlug}
                  onChange={e => setFormSlug(e.target.value)}
                  helperText="Leave empty to generate automatically from name."
                />
              </div>

              <div style={{ display: "grid", gridTemplateColumns: "2fr 1fr 1fr", gap: "14px", marginBottom: "14px" }}>
                <TextField
                  select
                  label="Parent Category"
                  fullWidth
                  variant="outlined"
                  size="small"
                  value={formParentId}
                  onChange={e => {
                    setFormParentId(e.target.value);
                    const parent = flatCategories.find(c => c._id === e.target.value);
                    if (parent) {
                      setFormLevel(parent.level + 1);
                      setFormType(parent.level === 1 ? 'SUBCATEGORY' : 'PRODUCT_TYPE');
                    } else {
                      setFormLevel(1);
                      setFormType('CATEGORY');
                    }
                  }}
                >
                  <MenuItem value="">— None (Root Category Level 1) —</MenuItem>
                  {flatCategories.filter(c => c.level < 3 && (!editingCategory || c._id !== editingCategory._id)).map(cat => (
                    <MenuItem key={cat._id} value={cat._id}>
                      {cat.level === 1 ? `[L1] ${cat.name}` : `    ↳ [L2] ${cat.name}`}
                    </MenuItem>
                  ))}
                </TextField>

                <TextField
                  select
                  label="Status"
                  fullWidth
                  variant="outlined"
                  size="small"
                  value={formStatus}
                  onChange={e => setFormStatus(e.target.value)}
                >
                  <MenuItem value="active">Active</MenuItem>
                  <MenuItem value="inactive">Inactive</MenuItem>
                  <MenuItem value="archived">Archived</MenuItem>
                </TextField>

                <TextField
                  label="Display Order"
                  type="number"
                  fullWidth
                  variant="outlined"
                  size="small"
                  value={formDisplayOrder}
                  onChange={e => setFormDisplayOrder(e.target.value)}
                />
              </div>

              <TextField
                label="Description"
                multiline
                rows={2}
                fullWidth
                variant="outlined"
                size="small"
                value={formDescription}
                onChange={e => setFormDescription(e.target.value)}
                placeholder="Explain what products belong in this department..."
                style={{ marginBottom: "16px" }}
              />

              {/* Attributes Builder Section */}
              <div style={{ background: "#fbfaf8", border: "1px solid #edf0f4", borderRadius: "10px", padding: "14px", marginBottom: "14px" }}>
                <strong style={{ fontSize: "13px", color: "#111315", display: "block", marginBottom: "8px" }}>
                  Category-Specific Structured Attributes
                </strong>

                <div style={{ display: "flex", flexDirection: "column", gap: "6px", marginBottom: "12px" }}>
                  {formAttributes.map((attr, i) => (
                    <div key={i} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", background: "#ffffff", padding: "8px 12px", borderRadius: "6px", border: "1px solid #edf0f4", fontSize: "12.5px" }}>
                      <div>
                        <strong>{attr.name}</strong> <span style={{ color: "#6b7280" }}>({attr.type}{attr.isRequired ? ', required' : ''})</span>
                        {attr.options?.length > 0 && <span style={{ color: "#6b7280", display: "block", fontSize: "11px" }}>Options: {attr.options.join(', ')}</span>}
                      </div>
                      <IconButton size="small" onClick={() => handleRemoveAttribute(i)}>
                        <DeleteOutlineIcon fontSize="small" style={{ color: "#dc2626" }} />
                      </IconButton>
                    </div>
                  ))}
                  {formAttributes.length === 0 && (
                    <span style={{ fontSize: "12px", color: "#9ca3af" }}>No specific attributes defined for this category.</span>
                  )}
                </div>

                <div style={{ display: "grid", gridTemplateColumns: "1.5fr 1fr 2fr auto auto", gap: "8px", alignItems: "center" }}>
                  <input
                    type="text"
                    placeholder="Attribute Name (e.g. Size, Color)"
                    style={{ fontSize: "12.5px", padding: "6px 10px", border: "1px solid #d1d5db", borderRadius: "6px" }}
                    value={newAttrName}
                    onChange={e => setNewAttrName(e.target.value)}
                  />
                  <select
                    style={{ fontSize: "12.5px", padding: "6px 8px", border: "1px solid #d1d5db", borderRadius: "6px" }}
                    value={newAttrType}
                    onChange={e => setNewAttrType(e.target.value)}
                  >
                    <option value="text">Text</option>
                    <option value="select">Dropdown Select</option>
                    <option value="number">Number</option>
                  </select>
                  <input
                    type="text"
                    placeholder="Options comma-separated"
                    style={{ fontSize: "12.5px", padding: "6px 10px", border: "1px solid #d1d5db", borderRadius: "6px" }}
                    value={newAttrOptions}
                    onChange={e => setNewAttrOptions(e.target.value)}
                  />
                  <label style={{ fontSize: "11.5px", display: "flex", alignItems: "center", gap: "4px", whiteSpace: "nowrap" }}>
                    <input
                      type="checkbox"
                      checked={newAttrRequired}
                      onChange={e => setNewAttrRequired(e.target.checked)}
                    />
                    Required
                  </label>
                  <Button
                    variant="outlined"
                    size="small"
                    onClick={handleAddAttribute}
                    style={{ fontSize: "11.5px", textTransform: "none" }}
                  >
                    + Add
                  </Button>
                </div>
              </div>

              {/* Controlled Suggested Tags Section */}
              <div style={{ background: "#fbfaf8", border: "1px solid #edf0f4", borderRadius: "10px", padding: "14px" }}>
                <strong style={{ fontSize: "13px", color: "#111315", display: "block", marginBottom: "8px" }}>
                  Standard Controlled Tags
                </strong>
                <div style={{ display: "flex", flexWrap: "wrap", gap: "6px", marginBottom: "8px" }}>
                  {formTags.map((t, idx) => (
                    <Chip
                      key={idx}
                      label={`#${t}`}
                      size="small"
                      onDelete={() => handleRemoveTag(t)}
                    />
                  ))}
                </div>
                <div style={{ display: "flex", gap: "8px" }}>
                  <input
                    type="text"
                    style={{ maxWidth: "260px", fontSize: "12.5px", padding: "6px 10px", border: "1px solid #d1d5db", borderRadius: "6px" }}
                    placeholder="Add tag (e.g. Leather, Formal)"
                    value={tagInput}
                    onChange={e => setTagInput(e.target.value)}
                    onKeyDown={handleAddTag}
                  />
                  <Button variant="outlined" size="small" onClick={handleAddTag} style={{ fontSize: "11.5px", textTransform: "none" }}>
                    + Add Tag
                  </Button>
                </div>
              </div>
            </DialogContent>
            <DialogActions style={{ padding: "14px 20px", borderTop: "1px solid #edf0f4" }}>
              <Button onClick={() => setIsModalOpen(false)} style={{ textTransform: "none" }}>
                Cancel
              </Button>
              <Button type="submit" variant="contained" style={{ background: "#8b2626", color: "#ffffff", fontWeight: "800", textTransform: "none" }}>
                Save Category
              </Button>
            </DialogActions>
          </form>
        </Dialog>

        {/* MODAL 2: MERGE CATEGORIES */}
        <Dialog open={isMergeModalOpen} onClose={() => setIsMergeModalOpen(false)} maxWidth="sm" fullWidth>
          <DialogTitle style={{ borderBottom: "1px solid #edf0f4" }}>
            <strong>Merge Duplicate Categories</strong>
          </DialogTitle>
          <form onSubmit={handleMergeSubmit}>
            <DialogContent style={{ padding: "20px" }}>
              <p style={{ fontSize: "13px", color: "#4b5563", marginBottom: "16px" }}>
                Merging will reassign all active products and subcategories from the source category into the destination target category, and archive the source category safely.
              </p>
              <div style={{ display: "flex", flexDirection: "column", gap: "14px" }}>
                <TextField
                  select
                  label="Source Duplicate Category (To Be Archived) *"
                  fullWidth
                  variant="outlined"
                  size="small"
                  value={mergeSourceId}
                  onChange={e => setMergeSourceId(e.target.value)}
                >
                  <MenuItem value="">— Select Source —</MenuItem>
                  {flatCategories.map(c => (
                    <MenuItem key={c._id} value={c._id}>
                      {c.name} ({c.level === 1 ? 'Category' : c.level === 2 ? 'Subcategory' : 'Product Type'})
                    </MenuItem>
                  ))}
                </TextField>

                <TextField
                  select
                  label="Target Primary Category (To Keep) *"
                  fullWidth
                  variant="outlined"
                  size="small"
                  value={mergeTargetId}
                  onChange={e => setMergeTargetId(e.target.value)}
                >
                  <MenuItem value="">— Select Target —</MenuItem>
                  {flatCategories.filter(c => c._id !== mergeSourceId).map(c => (
                    <MenuItem key={c._id} value={c._id}>
                      {c.name} ({c.level === 1 ? 'Category' : c.level === 2 ? 'Subcategory' : 'Product Type'})
                    </MenuItem>
                  ))}
                </TextField>
              </div>
            </DialogContent>
            <DialogActions style={{ padding: "14px 20px", borderTop: "1px solid #edf0f4" }}>
              <Button onClick={() => setIsMergeModalOpen(false)} style={{ textTransform: "none" }}>
                Cancel
              </Button>
              <Button type="submit" variant="contained" style={{ background: "#8b2626", color: "#ffffff", fontWeight: "800", textTransform: "none" }}>
                Merge &amp; Reassign Products
              </Button>
            </DialogActions>
          </form>
        </Dialog>
      </main>
    </div>
  );
}

export default CategoryManager;
